import { Tooltip } from '@bp/ui-components';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { FC, Fragment, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '../../../hooks/useConfirm';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { useDeleteTeachingBlockCardsMutation } from '../../../types/planung-graphql-client-defs';
import { groupByArray } from '../../../utils/arrayFunc';
import { showUserErrorToast } from '../../../utils/toast';
import { useTeachingBlockStore } from '../Contexts/TeachingBlockContext';
import { cardMatchesSearch } from '../cardSearch';
import { TeachingBlockCard } from '../TeachingBlockCard/TeachingBlockCard';
import { TeachingBlockCardType } from '../types';
import styles from './CardPicker.module.scss';

export const CardPicker: FC = observer(() => {
  const store = useTeachingBlockStore();
  const { t } = useTranslation();
  const teachingBlockCardContext = useMemorizedCacheTag('TEACHING_BLOCK_CARDS');
  const [, deleteCards] = useDeleteTeachingBlockCardsMutation();
  const { confirm, ConfirmationDialog } = useConfirm();

  // Löschen nur im Ablagestapel: im Raum-Kontext liegen hier gesteckte Karten. Das Board lädt die
  // Karten danach selbst neu (der Context der Mutation verwirft das Lesemodell der Version).
  const canDelete = !store.readonly && store.gridContext !== 'rooms';

  const deleteFromStack = async (stack: TeachingBlockCardType[], all: boolean) => {
    const deletable = stack.filter((card) => !card.locked && !card.startDate);
    const cards = all ? deletable : deletable.slice(0, 1);
    if (cards.length === 0) return;
    if (
      all &&
      !(await confirm({
        message: t('pinboard.actions.deleteCardsConfirm', {
          count: cards.length,
          subject: cards[0].subject?.name ?? '',
        }),
      }))
    ) {
      return;
    }
    if (store.pickedCard && cards.some((card) => card.uuid === store.pickedCard?.card.uuid)) {
      store.pickCard(null);
    }
    const response = await deleteCards({ cardUuids: cards.map((card) => card.uuid) }, teachingBlockCardContext);
    if (response.error) {
      showUserErrorToast({ error: response.error });
    }
  };

  const matchesSearch = (card: TeachingBlockCardType) => cardMatchesSearch(card, store.cardSearchString);

  const getCards = () => {
    if (store.gridContext === 'rooms') {
      return store.allPlacedCards.filter((c) => c.rooms.length === 0).filter(matchesSearch);
    }
    return (
      store.selectedRows.length > 0
        ? store.freeCards.filter((card) => {
            return store.selectedRows.some((row) => {
              return store.isUuidPresentInTimetableCard(card, row.value);
            });
          })
        : store.freeCards
    ).filter(matchesSearch);
  };

  const bySubjectName = (a: TeachingBlockCardType[], b: TeachingBlockCardType[]) =>
    (a[0].subject?.name ?? '').localeCompare(b[0].subject?.name ?? '');

  // Raum-Kontext: KEINE Gruppierung – jede platzierte Karte steht für sich (sie hat eine eigene
  // Zeit-Position, die per Hover angezeigt wird). Sonst wie gehabt nach Fach + Dauer stapeln.
  let stacks: TeachingBlockCardType[][];
  if (store.gridContext === 'rooms') {
    stacks = getCards()
      .map((card) => [card])
      .sort(bySubjectName);
  } else {
    const cards: TeachingBlockCardType[][] = [];
    const lessonGroups = groupByArray(getCards(), (card) => card.lesson?.uuid);
    lessonGroups.forEach((lessonGroup) => {
      const durationGroups = groupByArray(lessonGroup, (group) => group.duration);
      durationGroups.forEach((durationGroup) => cards.push(durationGroup));
    });
    stacks = cards.sort(bySubjectName);
  }

  const pickedUuid = store.pickedCard?.card.uuid ?? null;

  // Gesteckte Position (Zeitraum) der Karte – im Raum-Kontext als schneller Hover-Tooltip.
  const positionHint = (card: TeachingBlockCardType): string | undefined => {
    if (store.gridContext !== 'rooms' || !card.startDate || !card.endDate) return undefined;
    return `${dayjs(card.startDate).format('DD.MM.YYYY')} – ${dayjs(card.endDate).format('DD.MM.YYYY')}`;
  };

  const onCardClick = (event: MouseEvent<HTMLDivElement>, card: TeachingBlockCardType) => {
    event.stopPropagation();
    event.preventDefault();
    if (!card.locked) {
      // Raum-Kontext: Shift beim Aufnehmen → Kopiermodus (Karte in mehrere Räume setzen).
      store.pickCard(card, { x: event.clientX, y: event.clientY }, true, undefined, event.shiftKey);
    }
  };

  return (
    <>
      <div className={classNames(styles['card-picker'], 'has-scrollbar', 'drop-and-delete')}>
        {stacks.map((container) => {
          // Die Karte in der Hand bleibt in `freeCards` (bzw. im Raum-Stapel) und wird hier nur aus der
          // Anzahl herausgerechnet. War sie die letzte ihres Stapels, bleibt ein blasser Platzhalter
          // stehen, damit die folgenden Stapel nicht nachrücken.
          const rest = pickedUuid ? container.filter((c) => c.uuid !== pickedUuid) : container;
          if (rest.length === 0) {
            return (
              <TeachingBlockCard
                key={container[0].uuid}
                className={styles.placeholder}
                card={{ ...toJS(container[0]), availabilityWarnings: [] }}
                passive
              />
            );
          }
          const card = rest[0];
          const cardClasses = classNames({
            [styles.multi]: rest.length >= 2,
          });
          const hint = positionHint(card);

          const cardEl = (
            <TeachingBlockCard
              className={cardClasses}
              card={{
                ...toJS(card),
                availabilityWarnings: [],
              }}
              count={rest.length}
              hideTitle={!!hint}
              onClick={onCardClick}
              onHover={(isHovering, card) => {
                if (!isHovering && store.pickedCard) {
                  store.selectCard(store.pickedCard.card);
                } else {
                  store.selectCard(card);
                }
              }}
              passive={false}
              onDelete={canDelete ? (all) => void deleteFromStack(rest, all) : undefined}
            />
          );

          // Tooltip (ui-components) ist "teuer" → nur im Raum-Kontext rendern, wo die gesteckte
          // Position gebraucht wird. Sonst die Karte unverändert ausgeben.
          return hint ? (
            <Tooltip key={container[0].uuid} content={hint}>
              {cardEl}
            </Tooltip>
          ) : (
            <Fragment key={container[0].uuid}>{cardEl}</Fragment>
          );
        })}
      </div>
      <ConfirmationDialog />
    </>
  );
});
